import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import moment from "moment";
import { NotificationManager } from "react-notifications";
import { makeStyles } from "@mui/styles";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import { getMBMDashboard } from "services/ConfigServices";
import UserService from "services/UserService";
import styled from "styled-components";
import AdvancedSearchContext from "context/AdvancedSearchContext";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";

const HighchartsContainer = styled.div`
  margin-top: 1.5rem;
  margin-right: 1rem;
`;

const Dashboard = () => {
  const permis = JSON.parse(localStorage.getItem("permission")) || [];
  const mbm_admin = permis.includes("mbm_admin");
  const currentusertype = localStorage.getItem('userType');
  const appContext = useContext(AdvancedSearchContext);
  const { chartIdGroup, groupInfoListById } = appContext;
  const [dashboard, setDashboard] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [timeoutfunc, setTimeoutfunc] = useState("");

  let timeout;
  useEffect(() => {
    if (timeoutfunc) {
      clearTimeout(timeoutfunc);
      setTimeoutfunc();
    }
    axios
      .get(getMBMDashboard(chartIdGroup), {
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
        },
      })
      .then((res) => {
        setDashboard(res.data);
        setTimeoutfunc(setTimeout(() => {
          setRefresh(!refresh);
        }, 300000));
      });
  }, [chartIdGroup, refresh]);

  const optionsColumnChart = {
    chart: {
      zoomType: "x",
      type: "column",
      height: "350px",
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "Etat des équipements",
    },
    xAxis: {
      categories: ['Equipements', 'IoT', 'Passerelles LoRaWAN', 'AVOB Energy Box']
    },
    yAxis: [
      {
        min: 0,
        title: {
          text: 'Pourcentage'
        },
      },
    ],
    legend: {
      shadow: false,
      //enabled: false
    },
    tooltip: {
      headerFormat: '<b>{point.x}</b><br/>',
      pointFormat: '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}</b> ({point.percentage:.0f}%)<br/>'
    },
    plotOptions: {
      column: {
        stacking: 'percent',
        dataLabels: {
          enabled: true
        }
      }
    },
    series: [{
      name: 'Actifs',
      data: [dashboard?.equipmentsactives, dashboard?.iotactives, dashboard?.gatewayactives, dashboard?.avobboxactives],
      color: '#91C73C'
    }, {
      name: 'Inactifs',
      data: [dashboard?.equipmentsinactives, dashboard?.iotinactives, dashboard?.gatewayinactives, dashboard?.avobboxinactives],
      color: '#C53F3F'
    }, {
      name: 'Jamais vus',
      data: [dashboard?.equipmentsnever, dashboard?.iotnever, dashboard?.gatewaynever, dashboard?.avobboxnever],
      color: '#CFCFD8'
    }]
  };

  const optionsPieChart = {
    chart: {
      plotBackgroundColor: null,
      plotBorderWidth: null,
      plotShadow: false,
      type: 'pie',
      height: "350px",
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "Points connectés",
    },
    tooltip: {
      pointFormat: '{series.name}: <b>{point.y}</b> ({point.percentage:.1f}%)'
    },
    accessibility: {
      point: {
        valueSuffix: '%'
      }
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b>: {point.y}'
        },
        showInLegend: true
      }
    },
    legend: {
      shadow: false,
      //enabled: false
    },
    series: [{
      name: 'Nombre de points',
      colorByPoint: true,
      data: [{
        name: 'Monitorés',
        y: dashboard?.pointviewer,
        color: '#3E8FC6'
      }, {
        name: 'Pilotés',
        y: dashboard?.pointsaver,
        color: '#FF763A'
      }, {
        name: 'Non utilisés',
        y: dashboard?.pointinactives,
        color: '#CFCFD8'
      }]
    }]
  };

  const optionsColumnChart2 = {
    chart: {
      zoomType: "x",
      type: "column",
      height: "350px",
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "Etat des sessions",
    },
    xAxis: {
      categories: ['LoRaWAN', 'Modbus RTU', 'BacNet IP', 'Modbus IP', 'Modbus IP V2', 'S7Profinet', 'Mitsubishi XML']
    },
    yAxis: [
      {
        min: 0,
        title: {
          text: 'Pourcentage'
        },
      },
    ],
    legend: {
      shadow: false,
      //enabled: false
    },
    tooltip: {
      headerFormat: '<b>{point.x}</b><br/>',
      pointFormat: '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}</b> ({point.percentage:.0f}%)<br/>'
    },
    plotOptions: {
      column: {
        stacking: 'percent',
        dataLabels: {
          enabled: true
        }
      }
    },
    series: [{
      name: 'Actives',
      data: [dashboard?.sessionactives_5 || 0, dashboard?.sessionactives_3 || 0, dashboard?.sessionactives_6 || 0, dashboard?.sessionactives_7 || 0, dashboard?.sessionactives_9 || 0, dashboard?.sessionactives_8 || 0, dashboard?.sessionactives_10 || 0],
      color: '#91C73C'
    }, {
      name: 'Inactives',
      data: [dashboard?.sessioninactives_5 || 0, dashboard?.sessioninactives_3 || 0, dashboard?.sessioninactives_6 || 0, dashboard?.sessioninactives_7 || 0, dashboard?.sessioninactives_9 || 0, dashboard?.sessioninactives_8 || 0, dashboard?.sessioninactives_10 || 0],
      color: '#C53F3F'
    }, {
      name: 'Etat inconnu',
      data: [dashboard?.sessionnever_5 || 0, dashboard?.sessionnever_3 || 0, dashboard?.sessionnever_6 || 0, dashboard?.sessionnever_7 || 0, dashboard?.sessionnever_9 || 0, dashboard?.sessionnever_8 || 0, dashboard?.sessionnever_10 || 0],
      color: '#CFCFD8'
    }]
  };

  React.useEffect(() => {
    function handleResize() {
      console.log('resized to: ', window.innerWidth, 'x', window.innerHeight)

    }

    window.addEventListener('resize', handleResize)
  })

  let columns = [];

  return (
    <>
      <GridContainer>
        <GridItem xs={12} sm={12} md={6} lg={6}>
          <HighchartsContainer>
            <HighchartsReact highcharts={Highcharts} options={optionsColumnChart} />
          </HighchartsContainer>
        </GridItem>
        <GridItem xs={12} sm={12} md={6} lg={6}>
          <HighchartsContainer>
            <HighchartsReact highcharts={Highcharts} options={optionsPieChart} />
          </HighchartsContainer>
        </GridItem>
        {
          currentusertype == 1 ?
            <GridItem xs={12} sm={12} md={12} lg={12}>
              <HighchartsContainer>
                <HighchartsReact highcharts={Highcharts} options={optionsColumnChart2} />
              </HighchartsContainer>
            </GridItem>
            : <></>
        }

      </GridContainer>
    </>
  );
};

export default Dashboard;
